/** Deterministic pseudo-random in [0, 1) from a string seed (stable across reloads). */
function createSeededRng(seed) {
    let state = 0;
    const str = String(seed ?? "screenshot");
    for (let i = 0; i < str.length; i += 1) {
        state = (Math.imul(31, state) + str.charCodeAt(i)) | 0;
    }
    if (state === 0) state = 1;
    return () => {
        state = Math.imul(state ^ (state >>> 16), 2246822507);
        state = Math.imul(state ^ (state >>> 13), 3266489909);
        state ^= state >>> 16;
        return (state >>> 0) / 4294967296;
    };
}

function shuffleInPlace(arr, rng) {
    for (let i = arr.length - 1; i > 0; i -= 1) {
        const j = Math.floor(rng() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
}

function indexToRowCol(index, grid) {
    return { row: Math.floor(index / grid), col: index % grid };
}

/**
 * Candidate cells for empty slots: full inner board, excluding edge rings (no border pieces).
 */
function emptyCandidateCells(grid) {
    const borderInset = grid >= 10 ? 2 : 1;
    const cells = [];
    for (let row = borderInset; row < grid - borderInset; row += 1) {
        for (let col = borderInset; col < grid - borderInset; col += 1) {
            cells.push({
                index: row * grid + col,
                row,
                col,
            });
        }
    }
    return cells;
}

/**
 * Pick cells on the outer frame (and inner ring if needed) so placed pieces sit on
 * left / right / top / bottom rather than the center.
 */
function pickBorderPlacedIndices(grid, placedCount, seed) {
    if (grid <= 0 || placedCount <= 0) return [];
    const rng = createSeededRng(`${seed}-border`);
    const outer = [];
    const innerRing = [];
    const rest = [];
    for (let index = 0; index < grid * grid; index += 1) {
        const row = Math.floor(index / grid);
        const col = index % grid;
        const dist = Math.min(row, col, grid - 1 - row, grid - 1 - col);
        if (dist === 0) outer.push(index);
        else if (dist === 1) innerRing.push(index);
        else rest.push(index);
    }
    shuffleInPlace(outer, rng);
    shuffleInPlace(innerRing, rng);
    shuffleInPlace(rest, rng);
    return [...outer, ...innerRing, ...rest]
        .slice(0, Math.min(placedCount, grid * grid))
        .sort((a, b) => a - b);
}

function pickMiddleScatteredEmpties(grid, emptyCount, seed) {
    const rng = createSeededRng(`${seed}-middle`);
    const zone = emptyCandidateCells(grid);
    if (zone.length === 0 || emptyCount <= 0) return [];

    const indices = zone.map((c) => c.index);
    shuffleInPlace(indices, rng);
    return indices.slice(0, Math.min(emptyCount, indices.length)).sort((a, b) => a - b);
}

/**
 * Resolve empty ratio — fixed value or random pick within [min, max] (seeded).
 */
function resolveEmptyRatio({ emptyRatio, emptyRatioMin, emptyRatioMax, seed }) {
    if (
        Number.isFinite(emptyRatioMin) &&
        Number.isFinite(emptyRatioMax) &&
        emptyRatioMax > emptyRatioMin
    ) {
        const rng = createSeededRng(`${seed}-ratio`);
        return emptyRatioMin + rng() * (emptyRatioMax - emptyRatioMin);
    }
    return emptyRatio ?? 0.05;
}

/**
 * Split pieces between board (home positions) and tray.
 * Empty cells use `emptyRatio` or a random ratio in `emptyRatioMin`–`emptyRatioMax`.
 *
 * @param {number} total — grid × grid
 * @param {{ emptyCount?: number, emptyRatio?: number, emptyRatioMin?: number, emptyRatioMax?: number, seed?: string, grid?: number, emptyZone?: 'random' | 'middle' | 'border' }} opts
 * @returns {{ boardPieceIndices: number[], trayPieceIndices: number[] }}
 */
export function computeScreenshotPieceLayout(
    total,
    {
        emptyCount: fixedEmptyCount,
        emptyRatio = 0.05,
        emptyRatioMin,
        emptyRatioMax,
        seed = "screenshot",
        grid = 0,
        emptyZone = "random",
    } = {},
) {
    if (!Number.isFinite(total) || total <= 0) {
        return { boardPieceIndices: [], trayPieceIndices: [] };
    }

    const emptyCount = Number.isFinite(fixedEmptyCount)
        ? Math.min(total - 1, Math.max(0, fixedEmptyCount))
        : (() => {
              const ratio = resolveEmptyRatio({
                  emptyRatio,
                  emptyRatioMin,
                  emptyRatioMax,
                  seed,
              });
              return Math.min(total - 1, Math.max(0, Math.round(total * ratio)));
          })();

    let trayPieceIndices;
    if (emptyZone === "border" && grid > 0) {
        const placedCount = total - emptyCount;
        const boardPieceIndices = pickBorderPlacedIndices(grid, placedCount, seed);
        const boardSet = new Set(boardPieceIndices);
        trayPieceIndices = Array.from({ length: total }, (_, i) => i).filter(
            (i) => !boardSet.has(i),
        );
        return { boardPieceIndices, trayPieceIndices };
    }
    if (emptyZone === "middle" && grid > 0) {
        trayPieceIndices = pickMiddleScatteredEmpties(grid, emptyCount, seed);
    } else {
        const rng = createSeededRng(seed);
        const shuffled = shuffleInPlace(
            Array.from({ length: total }, (_, i) => i),
            rng,
        );
        trayPieceIndices = shuffled.slice(0, emptyCount).sort((a, b) => a - b);
    }

    const traySet = new Set(trayPieceIndices);
    const boardPieceIndices = Array.from({ length: total }, (_, i) => i).filter(
        (i) => !traySet.has(i),
    );
    return { boardPieceIndices, trayPieceIndices };
}

export function homePositionForPiece(pieceIndex, grid, boardSize) {
    const cellPx = boardSize / grid;
    const { row, col } = indexToRowCol(pieceIndex, grid);
    return {
        x: col * cellPx,
        y: row * cellPx,
    };
}

/**
 * Scatter pieces anywhere on the board (not home cells) — for marketing screenshots.
 * @returns {{ pieceIndex: number, x: number, y: number, rotateDeg: number }[]}
 */
export function buildScatteredBoardPlacements({
    pieceIndices,
    boardSize,
    cellPx,
    seed = "scatter",
    marginRatio = 0.06,
} = {}) {
    const indices = Array.isArray(pieceIndices) ? pieceIndices : [];
    if (indices.length === 0 || !(boardSize > 0) || !(cellPx > 0)) return [];

    const rng = createSeededRng(seed);
    const pieceSpan = cellPx * 1.15;
    const margin = boardSize * marginRatio;
    const maxX = Math.max(margin, boardSize - pieceSpan - margin);
    const maxY = Math.max(margin, boardSize - pieceSpan - margin);

    return indices.map((pieceIndex) => ({
        pieceIndex: Number(pieceIndex),
        x: margin + rng() * (maxX - margin),
        y: margin + rng() * (maxY - margin),
        rotateDeg: Math.round((rng() * 56 - 28) * 10) / 10,
        scattered: true,
    }));
}
